/**
 * Risk — thirteen rules, and the one that can cap the score.
 *
 * ── Why the cap is shown here and not folded into the score ──────────────────
 *
 * §6.3: risk is not a fourteenth factor. A weighted average lets a beautiful,
 * cheap, road-ready trailer with an advance-payment demand still land at 88,
 * because one negative factor at weight 6 cannot move a mean far enough. A cap
 * is arithmetically blunt and that is the point — explainable in one sentence,
 * impossible to dilute with good news elsewhere.
 *
 * **Only HIGH caps.** CAUTION annotates and does not touch the number, because
 * a caution cap would sit below the alert threshold and silently disable the
 * product's central mechanism for that listing — and `urgency_language` fires
 * on ordinary honest classifieds prose ("moving, need gone").
 *
 * A dismissed flag is still shown, greyed. Removing it from the panel would
 * make "somebody looked at this and decided it was fine" indistinguishable
 * from "nobody ever checked".
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import { dateText, TONES } from '../../lib/format'
import { riskLabel, riskLevelLabel } from '../../lib/domain'
import { Chip, Empty, Panel, S } from './ui'
import type { RiskFlag, RiskView } from './types'

const EN = {
  title: 'Risk',
  unavailable: 'Risk could not be loaded.',
  clear: 'Nothing is flagged.',
  clearWhy: 'Thirteen rules ran and none of them fired.',
  points: 'points',
  capNote: 'A HIGH level caps the score at 40. Caution annotates and leaves the number alone.',
  dismissed: 'Dismissed',
  raised: 'Raised',
  manual: 'Raised by hand',
  open: 'Open',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Riesgo',
  unavailable: 'No se pudo cargar el riesgo.',
  clear: 'No hay nada marcado.',
  clearWhy: 'Corrieron trece reglas y ninguna se activó.',
  points: 'puntos',
  capNote: 'Un nivel ALTO limita la puntuación a 40. Precaución solo anota y no toca el número.',
  dismissed: 'Descartada',
  raised: 'Levantada',
  manual: 'Levantada a mano',
  open: 'Abierta',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const levelTone = (level: string) => (level === 'high' ? TONES.danger : level === 'caution' ? TONES.warn : TONES.strong)

export default function RiskPanel({ risk, failed = false }: { risk: RiskView | null; failed?: boolean }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const flags = risk?.flags ?? []
  // Firing first, then raised-but-not-firing, then dismissed. The order is the
  // order somebody would want to read them in, not the order they were stored.
  const sorted = [...flags].sort((a, b) => {
    const rank = (f: RiskFlag) => (f.firing && !f.dismissed ? 0 : f.dismissed ? 2 : 1)
    return rank(a) - rank(b) || b.points - a.points
  })

  return (
    <Panel
      id="risk"
      title={t('title')}
      defaultOpen={risk?.level === 'high'}
      badge={
        risk ? <Chip tone={levelTone(risk.level)}>{riskLevelLabel(risk.level, locale)}</Chip> : undefined
      }
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : !risk || sorted.length === 0 ? (
        <>
          <Empty>{t('clear')}</Empty>
          <p style={S.note}>{t('clearWhy')}</p>
        </>
      ) : (
        <>
          {sorted.map((flag, index) => (
            <div
              key={flag.key}
              style={{
                padding: '10px 0',
                borderTop: index === 0 ? 'none' : '1px solid var(--line)',
                opacity: flag.dismissed ? 0.55 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{riskLabel(flag.key, locale)}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {flag.points} {t('points')}
                </span>
              </div>
              <p style={S.sentence}>{flag.sentence}</p>
              <div style={{ ...S.chipRow, marginTop: 6 }}>
                {flag.dismissed ? (
                  <Chip tone={TONES.quiet} outlined>
                    {t('dismissed')}
                    {flag.dismissedAt ? ` · ${dateText(flag.dismissedAt, locale)}` : ''}
                  </Chip>
                ) : (
                  <Chip tone={flag.firing ? TONES.danger : TONES.quiet} outlined={!flag.firing}>
                    {t('open')}
                    {flag.raisedAt ? ` · ${dateText(flag.raisedAt, locale)}` : ''}
                  </Chip>
                )}
                {flag.manual && (
                  <Chip tone={TONES.quiet} outlined>
                    {t('manual')}
                  </Chip>
                )}
              </div>
            </div>
          ))}
          <p style={S.note}>{t('capNote')}</p>
        </>
      )}
    </Panel>
  )
}
